import debug from 'debug'
import captureError from '@disco/utils/captureError'

import type { RumOverrides } from './datadog'
import { initDatadog } from './datadog'

/**
 * Configures shared dependencies and error handling for the application.
 *
 * Sets up production error tracking with Datadog and global unhandled rejection
 * handling. In development mode, enables debug logging for all modules.
 *
 * Features:
 * - Production: Initializes Datadog RUM and error capture
 * - Production: Sets up global unhandled promise rejection handling
 * - Development: Enables debug logging for all modules
 * - Filters out known/ignorable error patterns
 *
 * @example
 * ```typescript
 * // Call during application initialization
 * configureSharedDependencies()
 *
 * // Now unhandled promise rejections will be automatically captured
 * Promise.reject(new Error('This will be logged'))
 *
 * // Debug logging is enabled in development
 * const debug = require('debug')('MyModule')
 * debug('This will be logged in development')
 * ```
 */
export function configureSharedDependencies(options?: {
  datadogOptions?: RumOverrides
}) {
  if (process.env.NODE_ENV === 'production') {
    initDatadog(options?.datadogOptions)

    window.addEventListener('unhandledrejection', event => {
      if (event.reason) {
        captureError(new Error('unhandledrejection: ' + event.reason))
      } else {
        if (
          event.currentTarget &&
          event.target &&
          event.isTrusted &&
          event.type === 'unhandledrejection'
        ) {
          // ignore this error
          // reported as https://sentry.io/organizations/disco/issues/1709228767/
          return
        }
        captureError(new Error('unhandledrejection'), { extra: event })
      }
    })
  } else {
    debug.enable('*')
  }
}
